import { useMutation } from '@apollo/client'
import { type MutationBaseOptions } from '@apollo/client/core/watchQueryOptions'
import { Autocomplete, TextField } from '@mui/material'
import { type GridCellEditingApi } from '@mui/x-data-grid/models/api/gridEditingApi'
import { RosterSourceStatus } from '__generated__/graphql'
import { type FC } from 'react'
import { getSourceStatusLabel } from 'recruiter/components/SourceStatusPill'
import { UpdateSourceStatus } from 'recruiter/graphql/updateSourceStatus.gql'
import { useCandidateSearch } from 'recruiter/hooks'
import useToast from 'shared/components/Toast/useToast'
import { formatGraphQLError } from 'shared/utils'

interface SourceStatusEditCellProps {
    /** A reference to the MUI DataGrid cell editing API. Allows this component to manipulate the DataGrid it lives in. */
    api: GridCellEditingApi
    /** The candidate's active source status to display. */
    currentSourceStatus?: RosterSourceStatus | null
    /** An array (or a function that returns an array) that specifies which queries you want to refetch after this cell is successfully edited.  */
    refetchQueries?: MutationBaseOptions['refetchQueries']
    /** The roster ID of the candidate whose source status is being edited. */
    rosterCandidateId: string
    /** The ID of the row in the DataGrid that this cell belongs to. */
    rowId: string
    /** Whether the cell is disabled. */
    disabled?: boolean
}

const SourceStatusEditCell: FC<SourceStatusEditCellProps> = ({
    api,
    currentSourceStatus = RosterSourceStatus.NoStatus,
    refetchQueries,
    rosterCandidateId,
    rowId = rosterCandidateId,
    disabled = false,
}) => {
    const toast = useToast()
    const { refetch } = useCandidateSearch()

    const [updateSourceStatus] = useMutation(UpdateSourceStatus, {
        refetchQueries,
        onCompleted(data) {
            if (data.updateRosterCandidateSourceStatus.success) {
                toast({
                    title: 'Success',
                    message:
                        data.updateRosterCandidateSourceStatus.message ||
                        'Candidate source status updated successfully',
                    severity: 'success',
                })

                api.stopCellEditMode({
                    field: 'sourceStatus',
                    id: rowId,
                })

                refetch()
            } else {
                toast({
                    title: 'Error',
                    message:
                        data.updateRosterCandidateSourceStatus.message ||
                        'Error updating source status',
                    severity: 'error',
                })
            }
        },
        onError(error, clientOptions) {
            toast({
                title: 'Error',
                message: formatGraphQLError(
                    error,
                    'Error updating source status'
                ),
                severity: 'error',
            })
        },
    })

    return (
        <Autocomplete
            disableClearable
            fullWidth
            disabled={disabled}
            sx={{
                '& .MuiFormControl-root': {
                    height: '100%',
                },
                '& .MuiInputBase-root': {
                    height: '100%',
                    borderRadius: '0px',
                },
            }}
            renderInput={(props) => (
                <TextField
                    {...props}
                    fullWidth
                    inputProps={{
                        ...props.inputProps,
                        height: '100%',
                    }}
                />
            )}
            onChange={(event, sourceStatus) => {
                updateSourceStatus({
                    variables: {
                        rosterCandidateIds: [rosterCandidateId],
                        sourceStatus,
                    },
                })
            }}
            value={currentSourceStatus ?? RosterSourceStatus.NoStatus}
            options={Object.values(RosterSourceStatus)}
            getOptionLabel={(option) => getSourceStatusLabel(option)}
        />
    )
}

export default SourceStatusEditCell
